/**
 * Animation utilities for path interpolation and state management.
 */

import type { Coordinate } from '@/types'
import type { AnimationState, RouteTiming, SegmentTiming } from '../types/Animation.types'
import { ANIMATION_DEFAULTS } from '../types/Animation.types'
import { distance } from './Geometry.utils'
import { interpolateCubic, interpolateQuadratic } from './Bezier.utils'

const { DIRECTION_EPSILON } = ANIMATION_DEFAULTS

/**
 * Calculate position along a line segment at parameter t (0-1).
 * Linear interpolation: P(t) = P0 + t(P1 - P0)
 */
export function interpolateLine(
	p0: Coordinate,
	p1: Coordinate,
	t: number,
): Coordinate {
	const clampedT = Math.max(0, Math.min(1, t))
	return {
		x: p0.x + (p1.x - p0.x) * clampedT,
		y: p0.y + (p1.y - p0.y) * clampedT,
	}
}

/**
 * Calculate the position along a single segment at a given progress (0-1).
 */
export function getPositionAlongSegment(
	segment: SegmentTiming,
	progress: number,
): Coordinate {
	const { type, points } = segment
	const clampedProgress = Math.max(0, Math.min(1, progress))

	// Early return if no points
	if (points.length === 0) {
		return { x: 0, y: 0 }
	}

	const p0 = points[0]!
	const p1 = points[1] ?? p0
	const p2 = points[2] ?? p1
	const p3 = points[3] ?? p2

	switch (type) {
		case 'line':
			return interpolateLine(p0, p1, clampedProgress)

		case 'quadratic':
			return interpolateQuadratic(p0, p1, p2, clampedProgress)

		case 'cubic':
			return interpolateCubic(p0, p1, p2, p3, clampedProgress)

		default:
			return p0
	}
}

/**
 * Get position along an entire route at a given time (in ms).
 * Handles multiple segments with different lengths and timing.
 */
export function getPositionAlongRoute(
	routeTiming: RouteTiming,
	currentTime: number,
): Coordinate {
	const { segments, duration } = routeTiming

	if (segments.length === 0) {
		return { x: 0, y: 0 }
	}

	// Clamp time to valid range
	const clampedTime = Math.max(0, Math.min(duration, currentTime))

	// Find which segment contains this time
	for (const segment of segments) {
		if (clampedTime >= segment.startTime && clampedTime <= segment.endTime) {
			const segmentDuration = segment.endTime - segment.startTime
			const segmentProgress =
				segmentDuration > 0
					? (clampedTime - segment.startTime) / segmentDuration
					: 0

			return getPositionAlongSegment(segment, segmentProgress)
		}
	}

	// If past end, return last point of last segment
	const lastSegment = segments[segments.length - 1]
	if (!lastSegment || lastSegment.points.length === 0) {
		return { x: 0, y: 0 }
	}
	return lastSegment.points[lastSegment.points.length - 1] ?? { x: 0, y: 0 }
}

/**
 * Calculate the overall progress (0-1) based on current time.
 */
export function calculateProgress(
	currentTime: number,
	totalDuration: number,
): number {
	if (totalDuration <= 0) return 0
	return Math.max(0, Math.min(1, currentTime / totalDuration))
}

/**
 * Calculate time from progress.
 */
export function calculateTimeFromProgress(
	progress: number,
	totalDuration: number,
): number {
	const clampedProgress = Math.max(0, Math.min(1, progress))
	return clampedProgress * totalDuration
}

/**
 * Calculate the direction vector at a point along a segment.
 * Useful for orienting player sprites to face direction of movement.
 */
export function getDirectionAtSegment(
	segment: SegmentTiming,
	progress: number,
): Coordinate {
	const t1 = Math.max(0, progress - DIRECTION_EPSILON)
	const t2 = Math.min(1, progress + DIRECTION_EPSILON)

	const p1 = getPositionAlongSegment(segment, t1)
	const p2 = getPositionAlongSegment(segment, t2)

	const dx = p2.x - p1.x
	const dy = p2.y - p1.y
	const magnitude = distance(p1, p2)

	if (magnitude === 0) {
		return { x: 0, y: 1 } // Default to facing "up" (positive Y)
	}

	return {
		x: dx / magnitude,
		y: dy / magnitude,
	}
}

/**
 * Calculate the angle in radians from a direction vector.
 * 0 = facing right, PI/2 = facing up, etc.
 */
export function directionToAngle(direction: Coordinate): number {
	return Math.atan2(direction.y, direction.x)
}

/**
 * Determines if animation should be visible in UI.
 * Show animation when actively playing OR when user has seeked to any position.
 * This allows users to see the animation state even when paused.
 *
 * @param state - Current animation state
 * @returns true if animation should render, false for static view
 *
 * @example
 * // In a component that switches between animated and static views
 * const showAnimated = shouldShowAnimation(animation.state)
 * return showAnimated ? <AnimatedPlayRenderer /> : <StaticPlayRenderer />
 *
 * @example
 * // Controlling route progress visibility
 * <AnimatedRoute
 *   drawing={drawing}
 *   progress={progress}
 *   showProgress={shouldShowAnimation(state)}
 * />
 */
export function shouldShowAnimation(state: AnimationState): boolean {
	return state.isPlaying || state.currentTime > 0
}

